import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { 
  Box, 
  History, 
  CheckCircle, 
  XCircle,
  ScanBarcode,
  User,
  Clock,
  Building2,
  ArrowRight,
  RotateCcw,
  Wrench,
  Settings,
  AlertTriangle,
  Search,
  LogOut,
  Users,
  Timer,
  Smartphone,
  CreditCard,
  ArrowDownToLine,
  ArrowUpFromLine,
  Package,
  Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OVERVIEW_HEIGHT_KEY = "materialausgabe.overviewHeight";
const CARD_SCALE_KEY = "materialausgabe.deviceCardScale";

const STATUS_CONFIG = {
  AVAILABLE: {
    label: "Frei",
    color: "text-emerald-600",
    bg: "bg-emerald-50 border-emerald-200 hover:border-emerald-300",
    icon: CheckCircle,
    iconColor: "text-emerald-500",
    badgeClass: "bg-emerald-500"
  },
  LOANED: {
    label: "Ausgeliehen",
    color: "text-red-600",
    bg: "bg-red-50 border-red-200 hover:border-red-300",
    icon: XCircle,
    iconColor: "text-red-500",
    badgeClass: "bg-red-500"
  },
  DEFECT: {
    label: "Defekt",
    color: "text-amber-600",
    bg: "bg-amber-50 border-amber-200 hover:border-amber-300",
    icon: Wrench,
    iconColor: "text-amber-500",
    badgeClass: "bg-amber-500"
  },
  MAINTENANCE: {
    label: "Wartung",
    color: "text-blue-600",
    bg: "bg-blue-50 border-blue-200 hover:border-blue-300",
    icon: Settings,
    iconColor: "text-blue-500",
    badgeClass: "bg-blue-500"
  }
};

export default function Dashboard() {
  const { staff, logout } = useAuth();
  const [handhelds, setHandhelds] = useState([]);
  const [stats, setStats] = useState({ 
    handhelds: { total: 0, available: 0, loaned: 0, defect: 0, maintenance: 0, overdue: 0 },
    users: { total: 0, active: 0 },
    current_shift: "" 
  });
  const [loading, setLoading] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceHistory, setDeviceHistory] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [overviewHeight, setOverviewHeight] = useState(() => {
    try {
      const saved = Number(localStorage.getItem(OVERVIEW_HEIGHT_KEY));
      return saved >= 200 && saved <= 1000 && saved % 20 === 0 ? saved : 280;
    } catch {
      return 280;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(OVERVIEW_HEIGHT_KEY, String(overviewHeight));
    } catch {
      // Keep the control usable when storage is blocked.
    }
  }, [overviewHeight]);
  const [cardScale, setCardScale] = useState(() => {
    try {
      const saved = Number(localStorage.getItem(CARD_SCALE_KEY));
      return saved >= 80 && saved <= 160 && saved % 10 === 0 ? saved : 100;
    } catch {
      return 100;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(CARD_SCALE_KEY, String(cardScale));
    } catch {
      // Resizing still works when browser storage is unavailable.
    }
  }, [cardScale]);
  const visibleHandhelds = handhelds.filter(device => statusFilter === "ALL" || device.status === statusFilter);
  const [statusLoading, setStatusLoading] = useState(false);
  const [defectDescription, setDefectDescription] = useState("");
  
  // Issue/Return state
  const [activeTab, setActiveTab] = useState("issue");
  const [issueForm, setIssueForm] = useState({
    handheldSerial: "",
    badgeId: "",
    personalNumber: ""
  });
  const [returnForm, setReturnForm] = useState({
    handheldSerial: ""
  });
  const [scannedHandheld, setScannedHandheld] = useState(null);
  const [scannedUser, setScannedUser] = useState(null);
  const [issueLoading, setIssueLoading] = useState(false);
  const [returnLoading, setReturnLoading] = useState(false);
  
  // Refs for auto-focus
  const handheldInputRef = useRef(null);
  const badgeInputRef = useRef(null);
  const returnInputRef = useRef(null);

  // Handle status change for device
  const handleStatusChange = async (newStatus, description = null) => {
    if (!selectedDevice) return;
    
    setStatusLoading(true);
    try {
      const updateData = { status: newStatus };
      if (newStatus === "DEFECT" && description) {
        updateData.defect_description = description;
      }
      
      await axios.put(`${API}/handhelds/${selectedDevice.id}`, updateData);
      toast.success(`Status auf "${STATUS_CONFIG[newStatus]?.label}" geändert`);
      setShowDetailModal(false);
      setDefectDescription("");
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Ändern des Status";
      toast.error(message);
    } finally {
      setStatusLoading(false);
    }
  };

  const fetchHandhelds = useCallback(async () => {
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : "";
      const response = await axios.get(`${API}/handhelds${params}`);
      setHandhelds(response.data);
    } catch (error) {
      console.error("Error fetching handhelds:", error);
      if (error.response?.status === 401) {
        logout();
      }
    }
  }, [searchQuery, logout]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  const fetchDeviceHistory = useCallback(async (handheldId) => {
    try {
      const response = await axios.get(`${API}/handhelds/${handheldId}/history`);
      setDeviceHistory(response.data.entries || []);
    } catch (error) {
      console.error("Error fetching device history:", error);
      setDeviceHistory([]);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchHandhelds(), fetchStats()]);
    setLoading(false);
  }, [fetchHandhelds, fetchStats]);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      fetchHandhelds();
      fetchStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadData, fetchHandhelds, fetchStats]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchHandhelds();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchHandhelds]);

  const handleDeviceClick = async (device) => {
    setSelectedDevice(device);
    setShowDetailModal(true);
    await fetchDeviceHistory(device.id);
  };

  // Handheld lookup when serial is entered
  const lookupHandheld = async (serial) => {
    serial = serial.trim();
    if (!serial) {
      setScannedHandheld(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/handhelds/by-serial/${encodeURIComponent(serial)}`);
      setScannedHandheld(response.data);
    } catch (error) {
      setScannedHandheld(null);
    }
  };

  // User lookup when badge is entered
  const lookupUser = async (badgeId) => {
    if (!badgeId || badgeId.length < 3) {
      setScannedUser(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/users/by-badge/${badgeId}`);
      setScannedUser(response.data);
    } catch (error) {
      setScannedUser(null);
    }
  };

  // Handle issue form
  const handleIssue = async () => {
    if (!issueForm.handheldSerial.trim()) {
      toast.error("Bitte Gerätenamen oder Seriennummer eingeben");
      return;
    }
    if (!issueForm.badgeId && !issueForm.personalNumber) {
      toast.error("Bitte Ausweis scannen oder Personalnummer eingeben");
      return;
    }

    setIssueLoading(true);
    try {
      await axios.post(`${API}/loans/issue`, {
        handheld_serial_number: issueForm.handheldSerial.trim(),
        badge_id: issueForm.badgeId || null,
        personal_number: issueForm.personalNumber || null
      });
      toast.success("Gerät erfolgreich ausgegeben");
      setIssueForm({ handheldSerial: "", badgeId: "", personalNumber: "" });
      setScannedHandheld(null);
      setScannedUser(null);
      loadData();
      handheldInputRef.current?.focus();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler bei der Ausgabe";
      toast.error(message);
    } finally {
      setIssueLoading(false);
    }
  };

  // Handle return form
  const handleReturn = async () => {
    if (!returnForm.handheldSerial.trim()) {
      toast.error("Bitte Gerätenamen oder Seriennummer eingeben");
      return;
    }

    setReturnLoading(true);
    try {
      await axios.post(`${API}/loans/return`, {
        handheld_serial_number: returnForm.handheldSerial.trim()
      });
      toast.success("Gerät erfolgreich zurückgegeben");
      setReturnForm({ handheldSerial: "" });
      loadData();
      returnInputRef.current?.focus();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler bei der Rückgabe";
      toast.error(message);
    } finally {
      setReturnLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDuration = (durationInfo) => {
    if (!durationInfo) return "-";
    const { hours, minutes } = durationInfo;
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes} min`;
  };

  const isDeviceOverdue = (device) => {
    return device?.duration_info?.is_overdue || false;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-[1920px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-slate-900 rounded-md">
                <Box className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-slate-900">
                  Materialausgabe Management
                </h1>
                <p className="text-sm text-slate-500">{stats.current_shift}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Stats */}
              <div className="flex items-center gap-3 text-sm">
                <div className="flex items-center gap-1.5" title="Verfügbar">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                  <span className="font-mono font-medium">{stats.handhelds?.available || 0}</span>
                </div>
                <div className="flex items-center gap-1.5" title="Ausgeliehen">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                  <span className="font-mono font-medium">{stats.handhelds?.loaned || 0}</span>
                </div>
                {(stats.handhelds?.overdue || 0) > 0 && (
                  <div className="flex items-center gap-1.5 text-red-600 animate-pulse [animation-duration:4s]" title="Überfällig">
                    <Timer className="h-4 w-4" />
                    <span className="font-mono font-medium">{stats.handhelds?.overdue}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5" title="Defekt">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
                  <span className="font-mono font-medium">{stats.handhelds?.defect || 0}</span>
                </div>
                <div className="flex items-center gap-1.5" title="Wartung">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  <span className="font-mono font-medium">{stats.handhelds?.maintenance || 0}</span>
                </div>
              </div>
              
              <Separator orientation="vertical" className="h-8" />
              
              <Link to="/history">
                <Button variant="outline" size="sm" data-testid="history-btn">
                  <History className="h-4 w-4 mr-2" />
                  Historie
                </Button>
              </Link>
              
              <Link to="/users">
                <Button variant="outline" size="sm" data-testid="users-btn">
                  <Users className="h-4 w-4 mr-2" />
                  Benutzer
                </Button>
              </Link>
              
              {staff?.role === "ADMIN" && (
                <>
                  <Link to="/devices">
                    <Button variant="outline" size="sm" data-testid="devices-btn">
                      <Smartphone className="h-4 w-4 mr-2" />
                      Geräte
                    </Button>
                  </Link>
                  <Link to="/staff">
                    <Button variant="outline" size="sm" data-testid="staff-btn">
                      <Settings className="h-4 w-4 mr-2" />
                      Personal
                    </Button>
                  </Link>
                  <Link to="/settings/loans"><Button variant="outline" size="sm"><Timer className="h-4 w-4 mr-2" />Ausleihzeit</Button></Link>
                  <Link to="/settings/email">
                    <Button variant="outline" size="sm" data-testid="email-settings-btn">
                      <Mail className="h-4 w-4 mr-2" />
                      E-Mail
                    </Button>
                  </Link>
                </>
              )}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                    <User className="h-4 w-4" />
                    <span className="text-sm">{staff?.first_name}</span>
                    <Badge variant="outline" className="text-xs">{staff?.role}</Badge>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{staff?.first_name} {staff?.last_name}</p>
                    <p className="text-xs text-slate-500">{staff?.username}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1920px] mx-auto p-6 space-y-6">
        {/* Device Grid Section */}
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
              <Package className="h-5 w-5" />
              Geräteübersicht
              <Badge variant="outline" className="ml-2" aria-live="polite">{visibleHandhelds.length} Geräte</Badge>
            </h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Suche nach Gerät..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64"
                data-testid="search-input"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Geräte nach Status filtern">
            {[["ALL", "Alle"], ["AVAILABLE", "Verfügbar"], ["LOANED", "Ausgeliehen"], ["DEFECT", "Defekt"], ["MAINTENANCE", "Wartung"]].map(([value, label]) => (
              <Button
                key={value}
                type="button"
                size="sm"
                variant={statusFilter === value ? "default" : "outline"}
                aria-pressed={statusFilter === value}
                onClick={() => setStatusFilter(value)}
                data-testid={`status-filter-${value.toLowerCase()}`}
              >
                {label}
                <span className="ml-2 tabular-nums">{value === "ALL" ? handhelds.length : handhelds.filter(device => device.status === value).length}</span>
              </Button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Label htmlFor="device-card-size">Kachelgröße</Label>
            <span className="text-xs text-slate-500">Klein</span>
            <input
              id="device-card-size"
              type="range"
              min="80"
              max="160"
              step="10"
              value={cardScale}
              onChange={(event) => setCardScale(Number(event.target.value))}
              aria-valuetext={`${cardScale} Prozent`}
              className="w-36 sm:w-48 accent-slate-900 cursor-pointer"
            />
            <span className="text-xs text-slate-500">Groß</span>
            <output htmlFor="device-card-size" className="text-sm tabular-nums w-12">
              {cardScale} %
            </output>
            <Button type="button" variant="outline" size="sm" onClick={() => setCardScale(100)}>
              Standard
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Label htmlFor="device-overview-height">Fensterhöhe</Label>
            <span className="text-xs text-slate-500">Klein</span>
            <input
              id="device-overview-height"
              type="range"
              min="200"
              max="1000"
              step="20"
              value={overviewHeight}
              onChange={(event) => setOverviewHeight(Number(event.target.value))}
              aria-valuetext={`${overviewHeight} Pixel`}
              aria-controls="device-overview-content"
              className="w-36 sm:w-48 accent-slate-900 cursor-pointer"
            />
            <span className="text-xs text-slate-500">Groß</span>
            <output htmlFor="device-overview-height" className="text-sm tabular-nums w-16">
              {overviewHeight} px
            </output>
            <Button type="button" variant="outline" size="sm" onClick={() => setOverviewHeight(280)}>
              Standardhöhe
            </Button>
          </div>

          {loading ? (
            <div id="device-overview-content" className="flex items-center justify-center" style={{ height: overviewHeight }}>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : visibleHandhelds.length === 0 ? (
            <div id="device-overview-content" className="flex flex-col items-center justify-center gap-3 text-slate-500" style={{ height: overviewHeight }} role="status">
              <p>Keine Geräte für diese Auswahl gefunden.</p>
              <Button variant="outline" size="sm" onClick={() => { setStatusFilter("ALL"); setSearchQuery(""); }}>
                Filter zurücksetzen
              </Button>
            </div>
          ) : (
            <div id="device-overview-content" className="overflow-auto p-1" style={{ height: overviewHeight }}>
              <div 
                className="grid gap-2"
                style={{ gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${80 * cardScale / 100}px), 1fr))` }}
                data-testid="device-grid"
              >
                {visibleHandhelds.map((device) => {
                  const config = STATUS_CONFIG[device.status] || STATUS_CONFIG.AVAILABLE;
                  const StatusIcon = config.icon;
                  const overdue = isDeviceOverdue(device);
                  
                  return (
                    <button
                      key={device.id}
                      onClick={() => handleDeviceClick(device)}
                      data-testid={`device-${device.display_name}`}
                      style={{ padding: `${8 * cardScale / 100}px` }}
                      className={`
                        min-w-0 rounded-md border cursor-pointer text-left
                        transition-all duration-150 hover:shadow-md
                        ${config.bg}
                        ${overdue ? 'animate-pulse [animation-duration:4s] ring-2 ring-red-500' : ''}
                      `}
                    >
                      <div className="font-mono font-medium text-slate-700 break-words" style={{ fontSize: `${12 * cardScale / 100}px` }}>
                        {device.display_name}
                      </div>
                      <div className="mt-1 flex items-center gap-1">
                        <StatusIcon style={{ width: `${12 * cardScale / 100}px`, height: `${12 * cardScale / 100}px` }} className={`${overdue ? 'text-red-600' : config.iconColor}`} />
                      </div>
                      {device.duration_info && device.status === "LOANED" && (
                        <div style={{ fontSize: `${Math.max(10, 8 * cardScale / 100)}px` }} className={`mt-0.5 font-mono ${overdue ? 'text-red-600 font-bold' : 'text-slate-500'}`}>
                          {formatDuration(device.duration_info)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Issue / Return Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Issue Card */}
          <Card className="border-emerald-200">
            <CardHeader className="bg-emerald-50 border-b border-emerald-100">
              <CardTitle className="flex items-center gap-2 text-emerald-700">
                <ArrowUpFromLine className="h-5 w-5" />
                Gerät ausgeben
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {/* Handheld Scan */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  Gerätename oder Seriennummer
                </Label>
                <Input
                  ref={handheldInputRef}
                  value={issueForm.handheldSerial}
                  onChange={(e) => {
                    setIssueForm({...issueForm, handheldSerial: e.target.value});
                    lookupHandheld(e.target.value);
                  }}
                  placeholder="Gerätename oder Seriennummer scannen / eingeben"
                  className="font-mono h-12 text-lg"
                  data-testid="issue-handheld-input"
                />
                {scannedHandheld && (
                  <div className="p-2 bg-emerald-50 rounded border border-emerald-200 flex items-center justify-between">
                    <div>
                      <span className="font-mono font-medium">{scannedHandheld.display_name}</span>
                      <span className="text-sm text-slate-500 ml-2">{scannedHandheld.model}</span>
                    </div>
                    <Badge className={STATUS_CONFIG[scannedHandheld.status]?.badgeClass}>
                      {STATUS_CONFIG[scannedHandheld.status]?.label}
                    </Badge>
                  </div>
                )}
              </div>
              
              <Separator />
              
              {/* User Identification */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Ausweis scannen (Badge-ID)
                </Label>
                <Input
                  ref={badgeInputRef}
                  value={issueForm.badgeId}
                  onChange={(e) => {
                    setIssueForm({...issueForm, badgeId: e.target.value, personalNumber: ""});
                    lookupUser(e.target.value);
                  }}
                  placeholder="Ausweis auf Kartenleser legen..."
                  className="font-mono h-12 text-lg"
                  data-testid="issue-badge-input"
                />
                {scannedUser && (
                  <div className="p-2 bg-emerald-50 rounded border border-emerald-200">
                    <div className="font-medium">{scannedUser.first_name} {scannedUser.last_name}</div>
                    <div className="text-sm text-slate-500">{scannedUser.company} • {scannedUser.personal_number}</div>
                  </div>
                )}
              </div>
              
              <div className="text-center text-sm text-slate-500">- oder -</div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Personalnummer (manuell)
                </Label>
                <Input
                  value={issueForm.personalNumber}
                  onChange={(e) => setIssueForm({...issueForm, personalNumber: e.target.value, badgeId: ""})}
                  placeholder="Personalnummer eingeben..."
                  className="font-mono h-12"
                  disabled={!!issueForm.badgeId}
                  data-testid="issue-personal-input"
                />
              </div>
              
              <Button 
                onClick={handleIssue} 
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
                disabled={issueLoading || !issueForm.handheldSerial || (!issueForm.badgeId && !issueForm.personalNumber)}
                data-testid="issue-submit-btn"
              >
                {issueLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Ausgeben
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Return Card */}
          <Card className="border-blue-200">
            <CardHeader className="bg-blue-50 border-b border-blue-100">
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <ArrowDownToLine className="h-5 w-5" />
                Gerät zurückgeben
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  Gerätename oder Seriennummer
                </Label>
                <Input
                  ref={returnInputRef}
                  value={returnForm.handheldSerial}
                  onChange={(e) => setReturnForm({...returnForm, handheldSerial: e.target.value})}
                  placeholder="Gerätename oder Seriennummer scannen / eingeben"
                  className="font-mono h-12 text-lg"
                  data-testid="return-handheld-input"
                />
                <p className="text-sm text-slate-500">
                  Das System identifiziert automatisch den offenen Ausleihvorgang.
                </p>
              </div>
              
              <Button 
                onClick={handleReturn} 
                className="w-full h-12 bg-blue-600 hover:bg-blue-700"
                disabled={returnLoading || !returnForm.handheldSerial}
                data-testid="return-submit-btn"
              >
                {returnLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <RotateCcw className="h-5 w-5 mr-2" />
                    Zurückgeben
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Device Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh]" data-testid="detail-modal">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-3">
              <span className="font-mono text-2xl">{selectedDevice?.display_name}</span>
              {selectedDevice && (
                <Badge className={STATUS_CONFIG[selectedDevice.status]?.badgeClass}>
                  {STATUS_CONFIG[selectedDevice.status]?.label}
                </Badge>
              )}
              {isDeviceOverdue(selectedDevice) && (
                <Badge variant="destructive" className="animate-pulse [animation-duration:4s]">Überfällig!</Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedDevice?.model}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="info" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="info">Info</TabsTrigger>
              <TabsTrigger value="status">Status ändern</TabsTrigger>
              <TabsTrigger value="history">Historie</TabsTrigger>
            </TabsList>
            
            <TabsContent value="info" className="space-y-4 mt-4">
              {/* Device Info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Seriennummer</p>
                    <p className="font-mono font-medium">{selectedDevice?.serial_number}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Systemnummer</p>
                    <p className="font-mono font-medium">{selectedDevice?.display_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">MAC-Adresse</p>
                    <p className="font-mono">{selectedDevice?.mac_address || "-"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">IP-Adresse</p>
                    <p className="font-mono">{selectedDevice?.ip_address || "-"}</p>
                  </div>
                </div>
              </div>

              {/* Current Loan Info */}
              {selectedDevice?.status === "LOANED" && selectedDevice?.current_loan && (
                <div className={`rounded-lg p-4 space-y-3 ${isDeviceOverdue(selectedDevice) ? 'bg-red-100 border-2 border-red-500' : 'bg-red-50'}`}>
                  {isDeviceOverdue(selectedDevice) && (
                    <div className="flex items-center gap-2 text-red-600 font-bold animate-pulse [animation-duration:4s]">
                      <AlertTriangle className="h-5 w-5" />
                      <span>Ausleihdauer überschritten! (ab {selectedDevice.duration_info?.overdue_hours ?? 9} Std)</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-red-500" />
                    <div>
                      <p className="text-sm text-slate-500">Ausgeliehen an</p>
                      <p className="font-medium">{selectedDevice.current_loan.user_name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <ScanBarcode className="h-5 w-5 text-red-500" />
                    <div>
                      <p className="text-sm text-slate-500">Personalnummer</p>
                      <p className="font-mono font-medium">{selectedDevice.current_loan.user_personal_number}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-red-500" />
                    <div>
                      <p className="text-sm text-slate-500">Firma</p>
                      <p className="font-medium">{selectedDevice.current_loan.user_company}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-red-500" />
                    <div>
                      <p className="text-sm text-slate-500">Ausgegeben seit</p>
                      <p className="font-mono text-sm">{formatDate(selectedDevice.current_loan.start_timestamp)}</p>
                      <p className={`text-sm font-medium mt-1 ${isDeviceOverdue(selectedDevice) ? 'text-red-600' : 'text-slate-600'}`}>
                        Dauer: {formatDuration(selectedDevice.duration_info)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="pt-2 border-t border-red-200">
                    <p className="text-xs text-slate-500">
                      Ausgegeben von: {selectedDevice.current_loan.issued_by_staff_name} • {selectedDevice.current_loan.shift}
                    </p>
                  </div>
                </div>
              )}
            </TabsContent>
            
            {/* Status Change Tab */}
            <TabsContent value="status" className="space-y-4 mt-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600 mb-4">
                  Aktueller Status: <Badge className={STATUS_CONFIG[selectedDevice?.status]?.badgeClass}>{STATUS_CONFIG[selectedDevice?.status]?.label}</Badge>
                </p>
                
                {selectedDevice?.status === "LOANED" ? (
                  <div className="space-y-3">
                    <p className="text-sm text-amber-600 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Gerät ist ausgeliehen. Bitte erst zurückgeben, um Status zu ändern.
                    </p>
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/loans/return`, {
                            handheld_serial_number: selectedDevice.serial_number
                          });
                          toast.success("Gerät erfolgreich zurückgegeben");
                          setShowDetailModal(false);
                          loadData();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || "Fehler bei der Rückgabe");
                        }
                      }}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Jetzt zurückgeben
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-500 mb-3">Status ändern zu:</p>
                    
                    {/* Show existing defect description if status is DEFECT */}
                    {selectedDevice?.status === "DEFECT" && selectedDevice?.defect_description && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 mb-3">
                        <p className="text-xs text-amber-600 font-medium mb-1">Aktuelle Problembeschreibung:</p>
                        <p className="text-sm text-amber-800">{selectedDevice.defect_description}</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 gap-2">
                      <Button 
                        variant={selectedDevice?.status === "AVAILABLE" ? "default" : "outline"}
                        className={selectedDevice?.status === "AVAILABLE" ? "bg-emerald-600 hover:bg-emerald-700" : "hover:bg-emerald-50 hover:border-emerald-300"}
                        onClick={() => handleStatusChange("AVAILABLE")}
                        disabled={selectedDevice?.status === "AVAILABLE" || statusLoading}
                      >
                        <CheckCircle className="h-4 w-4 mr-2 text-emerald-500" />
                        Verfügbar
                      </Button>
                      
                      {/* Defect with description input */}
                      <div className="space-y-2">
                        <Button 
                          variant={selectedDevice?.status === "DEFECT" ? "default" : "outline"}
                          className={`w-full ${selectedDevice?.status === "DEFECT" ? "bg-amber-600 hover:bg-amber-700" : "hover:bg-amber-50 hover:border-amber-300"}`}
                          onClick={() => handleStatusChange("DEFECT", defectDescription)}
                          disabled={selectedDevice?.status === "DEFECT" || statusLoading}
                        >
                          <Wrench className="h-4 w-4 mr-2 text-amber-500" />
                          Defekt
                        </Button>
                        {selectedDevice?.status !== "DEFECT" && (
                          <Input
                            placeholder="Problembeschreibung (optional)"
                            value={defectDescription}
                            onChange={(e) => setDefectDescription(e.target.value)}
                            className="text-sm"
                          />
                        )}
                      </div>
                      
                      <Button 
                        variant={selectedDevice?.status === "MAINTENANCE" ? "default" : "outline"}
                        className={selectedDevice?.status === "MAINTENANCE" ? "bg-blue-600 hover:bg-blue-700" : "hover:bg-blue-50 hover:border-blue-300"}
                        onClick={() => handleStatusChange("MAINTENANCE")}
                        disabled={selectedDevice?.status === "MAINTENANCE" || statusLoading}
                      >
                        <Settings className="h-4 w-4 mr-2 text-blue-500" />
                        In Reparatur / Wartung
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="history" className="mt-4">
              <ScrollArea className="h-64">
                {deviceHistory.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Keine Historie vorhanden</p>
                ) : (
                  <div className="space-y-2">
                    {deviceHistory.map((loan) => (
                      <div key={loan.id} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <Badge className={loan.status === "OPEN" ? "bg-red-500" : "bg-emerald-500"}>
                            {loan.status === "OPEN" ? "Offen" : "Abgeschlossen"}
                          </Badge>
                          <span className="text-xs text-slate-500">{loan.shift}</span>
                        </div>
                        <div className="text-sm font-medium">{loan.user_name}</div>
                        <div className="text-xs text-slate-500">{loan.user_company} • {loan.user_personal_number}</div>
                        <div className="mt-2 text-xs text-slate-500">
                          <div>Ausgabe: {formatDate(loan.start_timestamp)} ({loan.issued_by_staff_name})</div>
                          {loan.end_timestamp && (
                            <div>Rückgabe: {formatDate(loan.end_timestamp)} ({loan.returned_by_staff_name})</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
